import { CompanyResponse, UpdateCompanyRequest } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation, useCompanyPermissions } from '../hooks';
import { addOrUpdateAllCompaniesCache } from '../queries/company-cache';

type UseUpdateCompanyActionProps = {
    companyId: string;
    /**
     * What to say when the save succeeds, as a whole sentence. The endpoint
     * updates the whole company while the caller usually changes one thing, so
     * the caller can be more specific than "Company updated" ever could.
     *
     * A sentence rather than a noun to slot into a template, because Spanish
     * agrees the participle with it: *imagen actualizada* but *nombre
     * actualizado*.
     */
    successMessage?: string;
};

type UseUpdateCompanyActionData = {
    /** Whether this viewer can change the company's settings right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    updateCompany: (data: UpdateCompanyRequest) => Promise<CompanyResponse>;
    loading: boolean;
    error: string;
};

/**
 * Changing the company's settings: whether this viewer may do it, why not when
 * they may not, and the call itself.
 *
 * Raises both toasts itself. On failure the reason is the information — the
 * caller pressing Save already knows what it was saving — so the specific
 * refusal is shown. On success the reason is nothing and what was saved is
 * everything, which is what `successMessage` is for.
 */
export function useUpdateCompanyAction(
    props: UseUpdateCompanyActionProps
): UseUpdateCompanyActionData {
    const { companyId, successMessage } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    const { canEditGeneralSettings } = useCompanyPermissions();

    const [isAllowed, reason] = useMemo(() => {
        if (!canEditGeneralSettings) {
            return [false, t('actions.update_company.reason_not_permitted')];
        }

        return [true, ''];
    }, [canEditGeneralSettings, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'COMPANY_NOT_FOUND')) {
                return t('shared.errors.company_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: UpdateCompanyRequest) =>
            openApiRef.current.companies
                .updateCompany(companyId, data)
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllCompaniesCache(queryClient, data.data.company);

            UIToast.success(
                successMessage ?? t('actions.update_company.success')
            );
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        updateCompany: (data: UpdateCompanyRequest): Promise<CompanyResponse> =>
            mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
